import type React from 'react';
import { useEffect } from 'react';

import { Box } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { SECURITY_DEPOSIT } from '../../../constants/index';
import { FEE_PAYMENT_TIME, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import FeePaymentTime from '../../../../shared/components/Widgets/FeePaymentTime';
import SecurityDepositCurrencyInput from './SecurityDeposit/SecurityDepositCurrencyInput';
import SecurityDepositYesAndNoExpansion from './SecurityDeposit/SecurityDepositYesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function SecurityDeposit_v10({ section, setSectionValues }: Props) {
  useEffect(() => {
    section.setCustomFieldValidator(SECURITY_DEPOSIT.FIELD_NAME.AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > SECURITY_DEPOSIT.BOUNDS.MAX_AMOUNT) {
      section
        .getField(SECURITY_DEPOSIT.FIELD_NAME.AMOUNT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(SECURITY_DEPOSIT.BOUNDS.MAX_AMOUNT));
      return false;
    }
    return true;
  }

  function disableSecurityDeposit() {
    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: false });
  }

  function enableSecurityDeposit() {
    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED]: true,
      [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: '',
    });
  }

  function onAmountChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({ [SECURITY_DEPOSIT.FIELD_NAME.AMOUNT]: value });
  }

  function onPayDueClick(event: React.ChangeEvent<HTMLInputElement>): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
    });
  }

  return (
    <SecurityDepositYesAndNoExpansion
      yesHandler={enableSecurityDeposit}
      noHandler={disableSecurityDeposit}
      yesActive={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.IS_ENABLED)}
    >
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'layout.tight', marginTop: 'layout.tight' }}>
        <SecurityDepositCurrencyInput
          dataTest="security-deposit-amount"
          onBlur={onAmountChange}
          onChange={onAmountChange}
          section={section}
        />
        <FeePaymentTime
          label="When is the security deposit due?"
          section={section}
          onChange={onPayDueClick}
          firstChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.UPON_SIGNING)}
          secondChecked={section.getFieldValue(SECURITY_DEPOSIT.FIELD_NAME.START_MOVE_IN_DATE)}
        />
      </Box>
    </SecurityDepositYesAndNoExpansion>
  );
}

SecurityDeposit_v10.description = 'Asks for security deposit, when the deposit is due';
